import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TEST_IDS } from "@/constants/testIds";
import Brand from "@/components/Brand";
import { motion } from "framer-motion";

export default function Onboarding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", age: "", gender: "" });
  const [saving, setSaving] = useState(false);

  // DPDPA §9 — StyleBay is available only to users 13+. Users 13-17 must
  // have parental consent. We block <13 here on the client (backend also
  // enforces) and show a guardian-consent note for 13-17.
  const ageNum = Number(form.age);
  const ageValid = !form.age || (ageNum >= 13 && ageNum <= 120);
  const ageMinor = form.age && ageNum >= 13 && ageNum < 18;
  const valid = form.name.trim() && form.age && form.gender && ageValid;

  // Optimistically flip `onboarded` locally so ProtectedRoute stops
  // bouncing the user back here, even if the server-side call fails on
  // the native APK. The real API calls still fire in the background.
  const markLocallyOnboarded = (patch = {}) => {
    try { localStorage.setItem("sb:onboarded", "1"); } catch { /* ignore */ }
    setUser((u) => ({ ...(u || {}), ...patch, onboarded: true }));
  };

  const finish = async () => {
    if (saving) return;
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      age: Number(form.age),
      gender: form.gender,
    };
    // 1. Flip local state so the user isn't stuck if the network is slow.
    markLocallyOnboarded(payload);
    // 2. Fire the persistence calls without awaiting — best-effort.
    //    We intentionally do NOT call refresh() here — that would race with
    //    the mark-onboarded POST and could overwrite the optimistic state
    //    with a stale onboarded=false response.
    (async () => {
      try {
        await api.patch("/users/me", payload);
        await api.post("/onboarding/profile", {
          full_name: payload.name,
          age: payload.age,
          gender: payload.gender,
        });
        await api.post("/users/me/mark-onboarded", {});
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Onboarding persistence failed (continuing anyway):", e);
        }
      }
    })();
    // 3. Navigate immediately.
    navigate("/home", { replace: true });
  };

  const skip = () => {
    if (saving) return;
    setSaving(true);
    markLocallyOnboarded();
    (async () => {
      try {
        await api.post("/users/me/mark-onboarded", {});
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("Skip mark-onboarded failed (continuing anyway):", e);
        }
      }
    })();
    navigate("/home", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-20">
        <div className="flex items-baseline gap-2">
          <Brand size="md" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Atelier</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <p className="eyebrow mt-12">Welcome aboard</p>
          <h1 className="editorial-h1 mt-3">Just the basics.</h1>
          <p className="mt-4 text-sm text-muted-foreground max-w-lg">
            Three quick details and you're in. Add measurements, photos and Style DNA later — we'll prompt you when the time is right.
          </p>
        </motion.div>

        <div className="mt-10 border border-border p-6 lg:p-10 bg-card">
          <div className="grid gap-6">
            <Field label="Your name">
              <Input
                data-testid="onboarding-name-input"
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="As you'd like to be styled"
                className="rounded-none h-12"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Age">
                <Input
                  data-testid="onboarding-age-input"
                  type="number" min="13" max="120"
                  value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                  className="rounded-none h-12"
                />
                {form.age && !ageValid && (
                  <p className="text-[10px] text-destructive mt-1.5" data-testid="onboarding-age-error">
                    StyleBay is only for users aged 13-120.
                  </p>
                )}
                {ageMinor && (
                  <p className="text-[10px] text-amber-600 mt-1.5" data-testid="onboarding-age-minor-notice">
                    You&apos;re under 18. Please confirm your parent/guardian
                    has consented to your use of StyleBay.
                  </p>
                )}
              </Field>
              <Field label="Gender">
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="onboarding-gender-select" className="rounded-none h-12">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="prefer-not">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <Button
            data-testid={TEST_IDS.onboarding.skip}
            variant="outline"
            onClick={skip}
            disabled={saving}
            className="rounded-none uppercase tracking-[0.2em] text-xs h-12 px-6"
          >Skip for now</Button>
          <Button
            data-testid={TEST_IDS.onboarding.finish}
            onClick={finish}
            disabled={!valid || saving}
            className="rounded-none uppercase tracking-[0.2em] text-xs h-12 px-8"
          >{saving ? "Entering…" : "Enter Stylebay"}</Button>
        </div>

        <p className="mt-8 text-xs text-muted-foreground text-center">
          Complete your profile later to unlock the best styling experience.
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="eyebrow">{label}</Label><div className="mt-2">{children}</div></div>;
}
